import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("failed"); return; }
    let timer;
    const poll = async () => {
      if (attempts.current >= 8) { setStatus("failed"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch {}
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, refresh]);

  return (
    <div className="studio-bg grain min-h-screen grid place-items-center px-5">
      <div className="glass rounded-3xl p-10 text-center max-w-md fade-up" data-testid="payment-result">
        {status === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-[#3ECF8E] animate-spin mx-auto" />
            <h1 className="font-display text-2xl font-bold mt-5">Confirmation du paiement…</h1>
            <p className="text-slate-400 mt-2">Un instant, nous créditons votre compte.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto" />
            <h1 className="font-display text-2xl font-bold mt-5">Paiement réussi !</h1>
            <p className="text-slate-400 mt-2">Vos crédits ont été ajoutés à votre compte.</p>
            <Button onClick={() => navigate("/studio")} className="mt-6 h-11 px-6 bg-[#3ECF8E] hover:bg-[#2fb879] text-black glow-btn rounded-full font-semibold" data-testid="go-studio-button">
              Aller au Studio
            </Button>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="w-16 h-16 text-red-400 mx-auto" />
            <h1 className="font-display text-2xl font-bold mt-5">Paiement non confirmé</h1>
            <p className="text-slate-400 mt-2">Si le montant a été débité, vos crédits arriveront sous peu.</p>
            <Button onClick={() => navigate("/pricing")} className="mt-6 h-11 px-6 bg-white/10 hover:bg-white/15 rounded-full font-semibold">
              Retour aux tarifs
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
